import qs from 'qs';

/**
 * @typedef {Object} ContentItem
 * @property {string} ContentItem.type - The type of the content item (e.g., 'video', 'image', 'folder').
 * @property {string} [ContentItem.fileId] - The file ID for assets like videos, images, or files.
 * @property {string} [ContentItem.content_id] - The content ID for other types of items.
 * @property {string} ContentItem.parent - The parent path for folder items.
 * @property {string} ContentItem.name - The name of the content item, used for folder URLs.
 */

/**
 * Returns the URL for a given content item based on its type and identifiers.
 * @param {ContentItem} item - The content item object.
 * @returns {string} The URL for the content item.
 * @throws {Error} If the content type is unknown.
 */
const getContentUrl = item => {
  switch (item.type) {
    case 'video':
    case 'image':
    case 'file':
      if (!item.fileId) {
        throw new Error(`Missing fileId for content: ${JSON.stringify(item)}`);
      }

      return `/assets/${item.fileId}`;

    case 'folder':
    case 'campaign':
    case 'sign':
    case 'playlist':
    case 'sequence':
    case 'schedule':
      if (!item.content_id) {
        throw new Error(`Missing content_id for content: ${JSON.stringify(item)}`);
      }

      return `/${item.type}s/${item.content_id}`;
    case 'location':
      if (!item.location_id) {
        throw new Error(`Missing location_id for location: ${JSON.stringify(item)}`);
      }

      return `/locations/${item.location_id}`;
    case 'device':
      if (!item.id) {
        throw new Error(`Missing id for device: ${JSON.stringify(item)}`);
      }

      return `/devices/${item.id}`;
    default:
      throw new Error(`Unknown content type: ${item.type}`);
  }
};

/**
 * Parses the given URL and returns an object representation of the query parameters.
 * @private
 */
function internalParseUrl(url) {
  return qs.parse(url, { ignoreQueryPrefix: true });
}

/**
 * Stringifies the given query object into a URL query string.
 * @private
 */
function internalStringifyUrl(query) {
  return qs.stringify(query, {
    addQueryPrefix: true,
    arrayFormat: 'repeat',
    encodeValuesOnly: true,
  });
}

/**
 * Gets the value of a URL parameter from the given URL.
 * @param {string} url - The URL to parse.
 * @param {string} param - The name of the parameter to get.
 * @param {string|null} [defaultValue=null] - The default value to return if the parameter is not found. In addition, if default is an array, the return value will always be an array.
 * @returns {string|null} The value of the URL parameter or the default value.
 */
function getUrlParam(url, param, defaultValue = null) {
  const query = internalParseUrl(url);
  const paramValue = query[param] || defaultValue;

  return Array.isArray(defaultValue) && !Array.isArray(paramValue) ? [paramValue] : paramValue;
}

/**
 * Adds a new value of a URL parameter in the given URL.
 * @param {string} url - The URL to modify.
 * @param {string} param - The name of the parameter to set.
 * @param {string} value - The value to set for the parameter.
 * @returns {string} The modified URL with the updated parameter.
 */
function addToUrlParam(url, param, value) {
  const currentValue = getUrlParam(url, param, []);
  const s = new Set(currentValue);

  s.add(value);

  return setUrlParam(url, param, [...s]);
}

/**
 * Removes a value from a URL parameter if exists in the given URL.
 * @param {string} url - The URL to modify.
 * @param {string} param - The name of the parameter to set.
 * @param {string} value - The value to remove from the parameter.
 * @returns {string} The modified URL with the updated parameter.
 */
function removeFromUrlParam(url, param, value) {
  const currentValue = getUrlParam(url, param, []);
  const s = new Set(currentValue);

  s.delete(value);

  return setUrlParam(url, param, [...s]);
}

/**
 * Sets a new value for a URL parameter from the given URL
 * @param {string} url - The URL to modify.
 * @param {string} param - The name of the parameter to set.
 * @param {string} value - The value to set for the parameter.
 * @returns {string} The modified URL with the updated parameter.
 */
function setUrlParam(url, param, value) {
  const query = internalParseUrl(url);

  query[param] = value;

  return internalStringifyUrl(query);
}

/**
 * Removes a URL parameter from the given URL.
 * @param {string} url - The URL to modify.
 * @param {string} param - The name of the parameter to remove.
 * @returns {string} The modified URL without the specified parameter.
 */
function unsetUrlParam(url, param) {
  const query = internalParseUrl(url);

  delete query[param];

  return internalStringifyUrl(query);
}

export default {
  getContentUrl,
  getUrlParam,
  setUrlParam,
  unsetUrlParam,
  addToUrlParam,
  removeFromUrlParam,
};
